import type { ReactNode } from "react";
import * as Sentry from "@sentry/react";
import * as stylex from "@stylexjs/stylex";

import {
  ANALYTIC_APPS,
  ANALYTIC_SUPER_PROPERTIES,
} from "@animus/observability";

import { ENVIRONMENT } from "../environment";
import { button, layout, page, spacing, text } from "../styles.stylex";

let initialized = false;

function initSentry(): void {
  if (initialized || !ENVIRONMENT.SENTRY) {
    return;
  }

  Sentry.init({
    dsn: ENVIRONMENT.SENTRY.DSN,
    environment: ENVIRONMENT.DEPLOY_ENVIRONMENT,
    ...(ENVIRONMENT.SENTRY_RELEASE && { release: ENVIRONMENT.SENTRY_RELEASE }),
    tracesSampleRate: ENVIRONMENT.DEPLOY_ENVIRONMENT === "production" ? 0.1 : 1,
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    initialScope: {
      tags: {
        [ANALYTIC_SUPER_PROPERTIES.$APP]: ANALYTIC_APPS.WEB,
        git_sha: ENVIRONMENT.GIT_SHA ?? "unknown",
      },
    },
  });

  initialized = true;
}

export function SentryProvider({ children }: { children: ReactNode }) {
  initSentry();

  if (!ENVIRONMENT.SENTRY) {
    return <>{children}</>;
  }

  return (
    <Sentry.ErrorBoundary
      fallback={({ error }) => (
        <div {...stylex.props(layout.centered, page.dark, page.padding)}>
          <div {...stylex.props(text.center)}>
            <h1 {...stylex.props(text.bold, text["2xl"], text.error)}>
              Something went wrong
            </h1>
            <p {...stylex.props(spacing.mt2, text.muted)}>
              {error instanceof Error
                ? error.message
                : "An unexpected error occurred"}
            </p>
            <button
              {...stylex.props(button.base, button.primary, spacing.mt4)}
              onClick={() => window.location.reload()}
              type="button"
            >
              Reload page
            </button>
          </div>
        </div>
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

export { Sentry };
